import type {
  EntradaDosFiltrosDaLista,
  FiltrosDaListaNaApi,
  PartesDosFiltrosDaLista,
  StatusDaAbaTodos,
} from '../types/filtrosDaListaTypes';
import type { CadenciaBucket, NegociacaoCasoStatus } from '../types/negociacoesTypes';
import type { NegociacoesTabValue } from '../types/negociacoesUiTypes';

// O barrel da feature publica o tipo a partir daqui, junto da função que o produz.
export type { FiltrosDaListaNaApi };

const ROTULOS_DE_INTERVENCAO_HUMANA = ['Aguardando intervenção humana'];

const STATUS_POR_ROTULO: Record<string, NegociacaoCasoStatus> = {
  'Aguardando envio': 'SCHEDULED',
  'Aguardando resposta': 'NEGOTIATION_STARTED',
  'Em negociação': 'ACTIVE',
  'Acordo fechado': 'AGREEMENT_CLOSED',
  'Proposta recusada': 'NO_AGREEMENT',
  Interrompida: 'CLOSED',
  Pausada: 'PAUSED',
  'Falha no envio': 'FAILED',
};

/**
 * "Morna" cobre WARM e ACTIVE: a régua de cadência do core separa as duas faixas, e a tela
 * sempre as mostrou juntas como uma temperatura só.
 */
export function cadenciaDasTemperaturas(temperaturas: string[]): CadenciaBucket[] | undefined {
  if (temperaturas.length === 0) return undefined;

  const buckets = new Set<CadenciaBucket>();
  for (const temperatura of temperaturas) {
    if (temperatura === 'Quente') buckets.add('HOT');
    if (temperatura === 'Fria') buckets.add('COLD');
    if (temperatura === 'Morna') {
      buckets.add('WARM');
      buckets.add('ACTIVE');
    }
  }

  const resultado = Array.from(buckets);
  return resultado.length > 0 ? resultado : undefined;
}

export function situacoesDoFiltro(situacoes: string[]): string[] | undefined {
  return situacoes.length === 0 ? undefined : situacoes;
}

/** Com as duas opções marcadas (ou nenhuma), a pendência não restringe a lista. */
export function pendenciaDoFiltro(pendencias: string[]): 'WITH' | 'WITHOUT' | undefined {
  if (pendencias.length !== 1) return undefined;
  return pendencias[0] === 'Com pendência' ? 'WITH' : 'WITHOUT';
}

/**
 * O filtro de status só vale na sub-aba "Todos": "Com pendência" e "Arquivadas" já são recortes
 * de status. "Aguardando intervenção humana" não é status do caso, e sim a flag `humanIntervention`.
 */
export function statusDaAbaTodos(
  activeTab: NegociacoesTabValue,
  rotulos: string[],
): StatusDaAbaTodos {
  const vazio: StatusDaAbaTodos = {
    status: undefined,
    statusIn: undefined,
    humanIntervention: undefined,
  };
  if (activeTab !== 'ALL' || rotulos.length === 0) return vazio;

  const comIntervencaoHumana = rotulos.some((rotulo) =>
    ROTULOS_DE_INTERVENCAO_HUMANA.includes(rotulo),
  );
  const humanIntervention = comIntervencaoHumana ? true : undefined;
  const selecionados = rotulos
    .filter((rotulo) => !ROTULOS_DE_INTERVENCAO_HUMANA.includes(rotulo))
    .map((rotulo) => STATUS_POR_ROTULO[rotulo])
    .filter((status): status is NegociacaoCasoStatus => Boolean(status));

  if (selecionados.length === 0) return { ...vazio, humanIntervention };
  if (selecionados.length === 1) return { ...vazio, status: selecionados[0], humanIntervention };
  return { ...vazio, statusIn: selecionados, humanIntervention };
}

/**
 * Monta os parâmetros a partir das partes já convertidas. Existe separado de
 * `filtrosDaListaParaApi` para o hook da Lista memorizar cada parte e só refazer a consulta quando
 * uma delas muda de fato.
 */
export function montarFiltrosDaLista({
  activeTab,
  search,
  cadenceBucketIn,
  situacaoIdIn,
  pendency,
  statusDaAba,
}: PartesDosFiltrosDaLista): FiltrosDaListaNaApi {
  const comuns: FiltrosDaListaNaApi = {
    search: search || undefined,
    cadenceBucketIn,
    situacaoIdIn,
  };
  if (activeTab === 'PENDING') return { ...comuns, pendency: 'WITH' };
  if (activeTab === 'ARCHIVED') return { ...comuns, archived: true, pendency };
  return {
    ...comuns,
    humanIntervention: statusDaAba.humanIntervention,
    status: statusDaAba.status,
    statusIn: statusDaAba.statusIn,
    pendency,
  };
}

/**
 * Converte a sub-aba, a busca e os filtros da Lista nos parâmetros da API. É a mesma regra para a
 * listagem de negociações e para a visão Assinaturas, que filtra pelos mesmos critérios do caso.
 */
export function filtrosDaListaParaApi({
  activeTab,
  filters,
  search,
}: EntradaDosFiltrosDaLista): FiltrosDaListaNaApi {
  return montarFiltrosDaLista({
    activeTab,
    search,
    cadenceBucketIn: cadenciaDasTemperaturas(filters.temperature),
    situacaoIdIn: situacoesDoFiltro(filters.situacao),
    pendency: pendenciaDoFiltro(filters.pendency),
    statusDaAba: statusDaAbaTodos(activeTab, filters.status),
  });
}
